import {
  LayoutType,
  LocalShapeElementModel,
  type MindmapElementModel,
  type MindmapNode,
  type MindmapRoot,
  type NodeSide,
} from '@blocksuite/affine-model';
import { TelemetryProvider } from '@blocksuite/affine-shared/services';
import { requestThrottledConnectedFrame } from '@blocksuite/affine-shared/utils';
import { Bound } from '@blocksuite/global/gfx';
import type { PointerEventState } from '@blocksuite/std';
import {
  type BoxSelectionContext,
  GfxElementModelView,
  GfxViewInteractionExtension,
} from '@blocksuite/std/gfx';

import { handleLayout } from './utils.js';

export class MindMapView extends GfxElementModelView<MindmapElementModel> {
  static override type = 'mindmap';

  private readonly _collapseButtons = new Map<string, LocalShapeElementModel>();

  /** Which node — and, for the root, which of its two sides — a button folds. */
  private readonly _buttonOwner = new Map<
    string,
    { nodeId: string; side?: NodeSide }
  >();

  /** Nodes whose hover handlers are wired, so two buttons don't wire them twice. */
  private readonly _nodeHoverWired = new Set<string>();

  private readonly _hoveredState = new Map<
    string,
    {
      button: boolean;
      node: boolean;
    }
  >();

  private _buttonId(nodeId: string, side?: NodeSide) {
    return side ? `collapse-btn-${nodeId}-${side}` : `collapse-btn-${nodeId}`;
  }

  /**
   * A balanced root grows branches both ways, so it gets a collapse button per
   * side — folding the branches above it must leave the ones below alone.
   */
  private _isBalancedRoot(node: MindmapNode) {
    return (
      node.id === this.model.tree.id &&
      (this.model.layoutType === LayoutType.BALANCE ||
        this.model.layoutType === LayoutType.BALANCE_VERTICAL)
    );
  }

  private _buttonsOf(nodeId: string) {
    return [...this._buttonOwner.entries()]
      .filter(([, owner]) => owner.nodeId === nodeId)
      .map(([id, owner]) => ({
        button: this._collapseButtons.get(id)!,
        side: owner.side,
      }))
      .filter(entry => entry.button);
  }

  /**
   *
   * @param node The mindmap node or its id to get the collapse button
   * @param side For the root of a balanced map, which side's button
   * @returns
   */
  getCollapseButton(node: MindmapNode | string, side?: NodeSide) {
    const id = typeof node === 'string' ? node : node.id;
    return this._collapseButtons.get(this._buttonId(id, side));
  }

  private _initCollapseButtons() {
    const updateButtons = requestThrottledConnectedFrame(() => {
      if (!this.isConnected) {
        return;
      }

      const visited = new Set<LocalShapeElementModel>();

      this.model.traverse(node => {
        if (this._isBalancedRoot(node)) {
          (['left', 'right'] as NodeSide[]).forEach(side => {
            const btn = this._updateCollapseButton(node, side);

            btn && visited.add(btn);
          });

          return;
        }

        const btn = this._updateCollapseButton(node);

        btn && visited.add(btn);
      });

      this._collapseButtons.forEach(btn => {
        if (!visited.has(btn)) {
          const owner = this._buttonOwner.get(btn.id);

          this.surface.deleteLocalElement(btn);
          this._collapseButtons.delete(btn.id);
          this._buttonOwner.delete(btn.id);

          if (owner && !this._buttonsOf(owner.nodeId).length) {
            this._hoveredState.delete(owner.nodeId);
          }
        }
      });
    });

    this.disposable.add(
      this.model.propsUpdated.subscribe(({ key }) => {
        if (key === 'layoutType' || key === 'style') {
          updateButtons();
        }
      })
    );

    this.disposable.add(
      this.surface.elementUpdated.subscribe(payload => {
        if (this.model.children.has(payload.id)) {
          if (payload.props['xywh']) {
            updateButtons();
          }
          if (
            payload.props['hidden'] !== undefined ||
            payload.props['opacity'] !== undefined
          ) {
            this._updateButtonVisibility(payload.id);
          }
        }
      })
    );

    this.model.children.observe(updateButtons);

    this.disposable.add(() => {
      this.model.children.unobserve(updateButtons);
    });

    updateButtons();
  }

  /** The branches a button folds: one side's, or all of the node's. */
  private _branchesOf(node: MindmapNode, side?: NodeSide) {
    return side ? this.model.getSideChildren(side) : node.children;
  }

  private _isCollapsed(node: MindmapNode, side?: NodeSide) {
    return side
      ? this.model.isSideCollapsed(side)
      : (node.detail.collapsed ?? false);
  }

  private _needToUpdateButtonStyle(options: {
    button: LocalShapeElementModel;
    node: MindmapNode;
    side?: NodeSide;
    updateKey?: boolean;
  }) {
    const { button, node, side } = options;
    const layout = this.model.getLayoutDir(node);
    const cacheKey = [
      side ?? '-',
      this._isCollapsed(node, side),
      this._branchesOf(node, side).length,
      layout,
      this.model.layoutType,
      node.element.xywh,
      this.model.style,
    ].join('-');

    if (button.cache.get('MINDMAP_COLLAPSE_BUTTON') === cacheKey) {
      return false;
    } else if (options.updateKey) {
      button.cache.set('MINDMAP_COLLAPSE_BUTTON', cacheKey);
    }

    return true;
  }

  private _setLayoutMethod() {
    this.model.setLayoutMethod(function (
      this: MindmapElementModel,
      tree: MindmapNode | MindmapRoot = this.tree,
      options: {
        applyStyle?: boolean;
        layoutType?: LayoutType;
        stashed?: boolean;
      } = {
        applyStyle: true,
        stashed: true,
      }
    ) {
      const { stashed, applyStyle, layoutType } = Object.assign(
        {
          applyStyle: true,
          calculateTreeBound: true,
          stashed: true,
        },
        options
      );

      const pop = stashed ? this.stashTree(tree) : null;
      handleLayout(this, tree, applyStyle, layoutType);
      pop?.();
    });
  }

  private _setVisibleOnSelection() {
    let lastNode: null | string = null;
    this.disposable.add(
      this.gfx.selection.slots.updated.subscribe(() => {
        const elm = this.gfx.selection.firstElement;

        if (lastNode) {
          this._updateButtonVisibility(lastNode);
        }

        if (
          this.gfx.selection.selectedElements.length === 1 &&
          elm?.id &&
          this.model.children.has(elm.id)
        ) {
          if (!this._buttonsOf(elm.id).length) {
            return;
          }

          this._updateButtonVisibility(elm.id);
          lastNode = elm.id;
        }
      })
    );
  }

  private _updateButtonVisibility(node: string) {
    const latestNode = this.model.getNode(node);

    this._buttonsOf(node).forEach(({ button, side }) => {
      if (!latestNode) {
        button.opacity = 0;
        button.hidden = true;
        return;
      }

      const hoveredState = this._hoveredState.get(node) ?? {
        button: false,
        node: false,
      };

      const hovered = hoveredState.button || hoveredState.node;
      const hasChildren = this._branchesOf(latestNode, side).length > 0;
      const notHidden = !latestNode.element.hidden;
      const isNodeSelected =
        this.gfx.selection.firstElement === latestNode.element;
      const collapsed = this._isCollapsed(latestNode, side);

      button.hidden = latestNode.element.hidden;
      button.opacity =
        (hasChildren && notHidden && (collapsed || isNodeSelected || hovered)
          ? 1
          : 0) * (latestNode.element.opacity ?? 1);
    });
  }

  /**
   * Sit the button on the edge the branches it folds actually leave from.
   *
   * A side button belongs to a balanced root, where the axis is set by the map's
   * own layout rather than by the node's direction — `getLayoutDir` on such a
   * root just echoes BALANCE, which names no edge at all.
   */
  private _collapseButtonBound(
    node: MindmapNode,
    side: NodeSide | undefined,
    nodeBound: Bound,
    buttonStyle: { width: number; height: number }
  ) {
    if (side) {
      const vertical = this.model.layoutType === LayoutType.BALANCE_VERTICAL;

      if (vertical) {
        // `right` is the downward half, `left` the upward one.
        return nodeBound.moveDelta(
          (nodeBound.w - buttonStyle.width) / 2,
          side === 'right' ? 6 + nodeBound.h : -6 - buttonStyle.height
        );
      }

      return nodeBound.moveDelta(
        side === 'right' ? 6 + nodeBound.w : -6 - buttonStyle.width,
        (nodeBound.h - buttonStyle.height) / 2
      );
    }

    const layout = this.model.getLayoutDir(node);

    return layout === LayoutType.TOP
      ? nodeBound.moveDelta(
          (nodeBound.w - buttonStyle.width) / 2,
          6 + nodeBound.h
        )
      : layout === LayoutType.BOTTOM
        ? nodeBound.moveDelta(
            (nodeBound.w - buttonStyle.width) / 2,
            -6 - buttonStyle.height
          )
        : nodeBound.moveDelta(
            layout === LayoutType.LEFT
              ? -6 - buttonStyle.width
              : 6 + nodeBound.w,
            (nodeBound.h - buttonStyle.height) / 2
          );
  }

  private _updateCollapseButton(node: MindmapNode, side?: NodeSide) {
    if (!node?.element) return null;

    const branches = this._branchesOf(node, side);

    if (branches.length === 0) return null;

    const id = this._buttonId(node.id, side);
    const alreadyCreated = this._collapseButtons.has(id);
    const collapseButton =
      this._collapseButtons.get(id) ||
      new LocalShapeElementModel(this.model.surface);
    const collapsed = this._isCollapsed(node, side);

    if (
      this._needToUpdateButtonStyle({
        button: collapseButton,
        node,
        side,
        updateKey: true,
      })
    ) {
      const style = this.model.styleGetter.getNodeStyle(
        node,
        this.model.getPath(node)
      );
      const buttonStyle = collapsed ? style.expandButton : style.collapseButton;

      Object.entries(buttonStyle).forEach(([key, value]) => {
        // @ts-expect-error key is string
        collapseButton[key as unknown] = value;
      });

      const nodeElementBound = node.element.elementBound;
      const buttonBound = this._collapseButtonBound(
        node,
        side,
        nodeElementBound,
        buttonStyle
      );

      buttonBound.w = buttonStyle.width;
      buttonBound.h = buttonStyle.height;

      collapseButton.responseExtension = [16, 16];
      collapseButton.xywh = buttonBound.serialize();
      collapseButton.groupId = this.model.id;
      collapseButton.text = collapsed ? branches.length.toString() : '';
    }

    if (!alreadyCreated) {
      collapseButton.id = id;
      collapseButton.opacity = !node.element.hidden && collapsed ? 1 : 0;

      this._collapseButtons.set(id, collapseButton);
      this.surface.addLocalElement(collapseButton);

      // One hover state per NODE, shared by both of a balanced root's buttons —
      // give each its own and the second would overwrite the first's entry and
      // strand it invisible.
      const hoveredState = this._hoveredState.get(node.id) ?? {
        button: false,
        node: false,
      };
      this._hoveredState.set(node.id, hoveredState);

      this._buttonOwner.set(id, { nodeId: node.id, side });

      const buttonView = this.gfx.view.get(id) as GfxElementModelView;
      const isOnElementBound = (evt: PointerEventState) => {
        const [x, y] = this.gfx.viewport.toModelCoord(evt.x, evt.y);

        return buttonView.model.includesPoint(
          x,
          y,
          { useElementBound: true },
          this.gfx.std.host
        );
      };

      buttonView.on('pointerenter', () => {
        hoveredState.button = true;
        this._updateButtonVisibility(node.id);
      });
      buttonView.on('pointermove', evt => {
        const latestNode = this.model.getNode(node.id);
        if (
          latestNode &&
          !latestNode.element.hidden &&
          this._branchesOf(latestNode, side).length > 0
        ) {
          if (isOnElementBound(evt)) {
            this.gfx.cursor$.value = 'pointer';
          } else {
            this.gfx.cursor$.value = 'default';
          }
        }
      });
      buttonView.on('pointerleave', () => {
        this.gfx.cursor$.value = 'default';

        hoveredState.button = false;
        this._updateButtonVisibility(node.id);
      });
      buttonView.on('click', evt => {
        const latestNode = this.model.getNode(node.id);
        const telemetry = this.gfx.std.getOptional(TelemetryProvider);

        if (latestNode && isOnElementBound(evt)) {
          if (telemetry) {
            telemetry.track('ExpandedAndCollapsed', {
              page: 'whiteboard editor',
              segment: 'mind map',
              type: this._isCollapsed(latestNode, side) ? 'expand' : 'collapse',
            });
          }

          if (side) {
            this.model.toggleCollapseSide(side, { layout: true });
          } else {
            this.model.toggleCollapse(latestNode, { layout: true });
          }
        }
      });

      if (!this._nodeHoverWired.has(node.id)) {
        this._nodeHoverWired.add(node.id);

        const nodeView = this.gfx.view.get(node.id) as GfxElementModelView;

        nodeView.on('pointerenter', () => {
          hoveredState.node = true;
          this._updateButtonVisibility(node.id);
        });
        nodeView.on('pointerleave', () => {
          hoveredState.node = false;
          this._updateButtonVisibility(node.id);
        });
      }
    } else {
      this._updateButtonVisibility(node.id);
    }

    return collapseButton;
  }

  override onBoxSelected(context: BoxSelectionContext) {
    const { box } = context;
    const bound = new Bound(box.x, box.y, box.w, box.h);

    return bound.contains(this.model.elementBound);
  }

  override onCreated(): void {
    this._setLayoutMethod();
    this._initCollapseButtons();
    this._setVisibleOnSelection();
  }

  override onDestroyed() {
    super.onDestroyed();
    this._collapseButtons.forEach(btn => {
      this.surface.deleteLocalElement(btn);
    });
  }
}

export const MindMapInteraction = GfxViewInteractionExtension<MindMapView>(
  MindMapView.type,
  {
    resizeConstraint: {
      allowedHandlers: [],
    },
    handleSelection: () => {
      return {
        onSelect(context) {
          const { model } = context;

          if (model.isLocked()) {
            return context.default(context);
          }

          return false;
        },
      };
    },
  }
);
